import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'react-hot-toast';
import { Mail, ArrowLeft, Shield, RefreshCw } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import bcrypt from 'bcryptjs';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  import.meta.env.VITE_LOCALHOST_URL
);

const EmailVerification = () => {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 dakika
  const [canResend, setCanResend] = useState(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/register');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }
    
    if (!/^[0-9]*$/.test(value)) {
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Otomatik olarak sonraki input'a geç
    if (value !== '' && index < 5) {
      document.getElementById(`code-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      document.getElementById(`code-${index - 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...verificationCode];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newCode[index] = char;
    });
    setVerificationCode(newCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const code = verificationCode.join('');
      
      // Doğrulama kodunu kontrol et
      const { data: verificationData, error: verificationError } = await supabase
        .from('verification_codes')
        .select('*')
        .eq('email', email)
        .eq('code', code)
        .eq('used', false)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (verificationError || !verificationData) {
        toast.error('Geçersiz veya süresi dolmuş doğrulama kodu');
        setIsLoading(false);
        return;
      }

      // Kayıt verilerini al
      const registrationData = verificationData.registration_data;

      // Profil fotoğrafını yükle
      let profilePhotoUrl = null;
      if (registrationData.profilePhoto) {
        const fileExt = registrationData.profilePhoto.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `profile-photos/${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('profile-photos')
          .upload(filePath, registrationData.profilePhoto);

        if (uploadError) {
          console.error('Profile photo upload error:', uploadError);
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('profile-photos')
            .getPublicUrl(filePath);
          profilePhotoUrl = publicUrl;
        }
      }

      // Şifreyi hashle
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(registrationData.password, salt);

      // Auth kullanıcısını oluştur
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: registrationData.email,
        password: registrationData.password
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!authData.user?.id) {
        throw new Error('Kullanıcı ID\'si alınamadı');
      }

      // Profili oluştur
      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert([{
          id: authData.user.id,
          first_name: registrationData.firstName,
          last_name: registrationData.lastName,
          email: registrationData.email,
          phone: registrationData.phone,
          country: registrationData.country,
          city: registrationData.city,
          national_id: registrationData.tcNo,
          password_hash: hashedPassword,
          birth_date: registrationData.birthDate,
          gender: registrationData.gender,
          job_position: registrationData.position,
          website: registrationData.website,
          address: registrationData.address,
          profile_photo_url: profilePhotoUrl,
          accepted_terms: registrationData.termsAccepted,
          accepted_kvkk: registrationData.kvkkAccepted,
          subscribed_newsletter: registrationData.newsletterSubscription,
          accepted_data_processing: registrationData.dataShareConsent
        }]);

      if (insertError) {
        // Profil fotoğrafını sil
        if (profilePhotoUrl) {
          await supabase.storage
            .from('profile-photos')
            .remove([profilePhotoUrl.split('/').pop()]);
        }
        console.error('Profile insert error:', insertError);
        throw new Error('Profil oluşturulamadı: ' + insertError.message);
      }

      // Doğrulama kodunu kullanıldı olarak işaretle
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('id', verificationData.id);

      toast.success('Hesabınız başarıyla oluşturuldu!');
      navigate('/signin');
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    setIsLoading(true);
    try {
      // Yeni kod oluştur
      const newCode = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 5);

      // Eski kodları geçersiz kıl
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('email', email)
        .eq('used', false);

      // Yeni kod oluştur
      const { error } = await supabase
        .from('verification_codes')
        .insert([{
          email,
          code: newCode,
          expires_at: expiresAt.toISOString(),
          registration_data: location.state?.registrationData
        }]);

      if (error) throw error;

      // Email gönder
      const response = await fetch(`${import.meta.env.VITE_LOCALHOST_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: email,
          subject: 'Email Doğrulama Kodu',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2563eb;">Email Doğrulama</h2>
              <p>Merhaba,</p>
              <p>Email doğrulama kodunuz:</p>
              <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0;">
                <span style="font-size: 32px; font-weight: bold; letter-spacing: 8px; color: #1f2937;">${newCode}</span>
              </div>
              <p>Bu kod 5 dakika süreyle geçerlidir.</p>
              <p>Bu işlemi siz yapmadıysanız, lütfen bu emaili görmezden gelin.</p>
            </div>
          `
        })
      });

      if (!response.ok) {
        throw new Error('Email gönderilemedi');
      }

      toast.success('Yeni doğrulama kodu gönderildi');
      setTimeLeft(300);
      setCanResend(false);
    } catch (error) {
      console.error('Resend error:', error);
      toast.error(error.message || 'Kod gönderilemedi');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${
      isDark
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
        : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
    } py-12 px-4 sm:px-6 lg:px-8`}>
      {/* Arka plan grid deseni */}
      <div className="absolute inset-0">
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${
          isDark ? 'opacity-10' : 'opacity-20'
        }`} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className={`${
          isDark
            ? 'bg-white/10 backdrop-blur-xl border-white/10'
            : 'bg-white/80 backdrop-blur-xl border-gray-200'
        } p-8 rounded-3xl border shadow-2xl`}>
          {/* Geri dön butonu */}
          <button
            onClick={() => navigate('/register')}
            className={`group mb-8 flex items-center text-sm font-medium ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            } transition-colors`}
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Kayıt sayfasına dön
          </button>

          <div className="text-center mb-8">
            <Shield className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
              Email Doğrulama
            </h2>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <Mail className="w-4 h-4 inline-block mr-1" />
              {email} adresine gönderilen 6 haneli kodu girin
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center space-x-3">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-12 h-12 text-center text-2xl font-semibold ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white'
                      : 'bg-white border-gray-200 text-gray-900'
                  } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                  required
                />
              ))}
            </div>

            <div className="text-center">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Kalan süre: {formatTime(timeLeft)}
              </p>
            </div>

            <div className="flex flex-col space-y-4">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={isLoading || verificationCode.some(digit => digit === '')}
                className={`group relative w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium shadow-lg transition-all duration-200 ${
                  (isLoading || verificationCode.some(digit => digit === ''))
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {isLoading ? 'Doğrulanıyor...' : 'Doğrula'}
              </motion.button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading || !canResend}
                className={`flex items-center justify-center space-x-2 py-2 px-4 text-sm font-medium rounded-xl ${
                  isDark
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                } transition-colors ${
                  (isLoading || !canResend) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                <span>Yeni kod gönder</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailVerification; 